// src/components/opportunities/OpportunitiesTab.jsx
//
// The Opportunities pipeline board. Extracted from MspOsApp.jsx, which was
// carrying this inline alongside every other tab.
//
// Two views of the same main area rather than a permanent split: the card grid,
// and one opportunity's detail. The Inbox panel sits to the right of both, so an
// email can be dragged onto a card from the board OR onto the open opportunity
// from its detail view — the drag source stays put while the main area changes.

import { useState, useMemo } from "react";
import {
  getLinkedProposals, getLinkedQuotes, getOpportunityColour,
  opportunityCounts, visibleOpportunities, pipelineStatusOf, isOpen,
  COLOUR_STYLE, CLOSED_STYLE, CLOSED_WON,
  closePatch, reopenPatch,
} from "./opportunityPipeline";
import { isLead } from "../proposals/docTypes";

/* Status pill colours, matching how Quoting and Proposals already paint their
   own status columns — a quote should not change colour depending on which
   screen you read it from. */
const ITEM_STATUS = {
  draft:    { fg: "var(--text3)", bg: "var(--s2)",       label: "Drafting" },
  sent:     { fg: "var(--warn)",  bg: "var(--warn-d)",   label: "Sent" },
  won:      { fg: "var(--ops)",   bg: "var(--ops-d)",    label: "Won" },
  lost:     { fg: "var(--danger)",bg: "var(--danger-d)", label: "Lost" },
  archived: { fg: "var(--text3)", bg: "var(--s3)",       label: "Archived" },
};
const itemStatus = (s) => ITEM_STATUS[s] || ITEM_STATUS.draft;

const fmtDate = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
};

/* An unparseable timestamp does NOT throw — `new Date("nonsense")` is a valid
   Date object whose toLocaleString returns the literal string "Invalid Date".
   A try/catch alone therefore prints that straight onto the email header, which
   is what the attached emails were showing. Checked with isNaN and degraded to
   the raw value, which at least carries whatever the sender actually sent. */
const fmtDateTime = (t) => {
  if (!t) return "";
  const d = new Date(t);
  if (isNaN(d.getTime())) return typeof t === "string" ? t : "";
  return d.toLocaleString("en-AU", {
    day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit",
  });
};

/* Pull an email off a drag event. Returns null for anything that isn't one, so
   a stray drag can't create a blank opportunity. */
const emailFromDrop = (e) => {
  const raw = e.dataTransfer.getData("emailItem");
  if (!raw) return null;
  try { return JSON.parse(raw); }
  catch (err) { console.warn("Bad email drop on opportunities", err); return null; }
};

export default function OpportunitiesTab({
  opportunities = [],
  proposals = [],
  quotes = [],
  atCompanies = [],
  selectedOpportunityId,
  setSelectedOpportunityId,
  createOpportunityManually,
  createOpportunityFromEmail,
  addEmailToOpportunity,
  removeEmailFromOpportunity,
  updateOpportunity,
  deleteOpportunity,
  onCreateProposal,
  onCreateQuote,
  onOpenProposal,
  onOpenQuote,
}) {
  const [showClosed, setShowClosed] = useState(false);

  const selected = opportunities.find((o) => o.id === selectedOpportunityId) || null;

  const visible = useMemo(
    () => visibleOpportunities(opportunities, showClosed),
    [opportunities, showClosed],
  );

  const openCount   = opportunities.filter(isOpen).length;
  const closedCount = opportunities.length - openCount;

  /* Closing the detail view returns to whichever list the opportunity belongs
     to. Without this, closing a deal from its detail view drops you back on a
     board that no longer contains it, which reads as the record vanishing. */
  const backToBoard = () => setSelectedOpportunityId(null);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "var(--bg)" }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", flexShrink: 0,
                    background: "white", display: "flex", alignItems: "center",
                    justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ fontFamily: "var(--ff-head)", fontWeight: 700, fontSize: 17,
                        color: "var(--text)", letterSpacing: "-0.3px" }}>💡 Opportunities</div>
          <div style={{ fontSize: 11, color: "var(--text3)" }}>
            <span style={{ color: "var(--danger)", fontWeight: 600 }}>{openCount}</span> open
            <span> · </span>
            <span style={{ color: "var(--text2)", fontWeight: 600 }}>{closedCount}</span> closed
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11,
                          color: "var(--text2)", cursor: "pointer", userSelect: "none" }}
                 title="Closed opportunities are hidden by default">
            <input type="checkbox" checked={showClosed} style={{ cursor: "pointer", margin: 0 }}
                   onChange={(e) => { setShowClosed(e.target.checked); setSelectedOpportunityId(null); }} />
            Show closed
          </label>
          <button className="btn btn-strat" style={{ fontSize: 11 }}
                  onClick={createOpportunityManually}>+ New opportunity</button>
        </div>
      </div>

      {/* ── Main area: board or detail ─────────────────────────────────── */}
      {selected ? (
        <OpportunityDetail
          opportunity={selected}
          proposals={proposals}
          quotes={quotes}
          atCompanies={atCompanies}
          onBack={backToBoard}
          onPatch={(patch) => updateOpportunity(selected.id, patch)}
          onDelete={() => { deleteOpportunity(selected.id); }}
          onDropEmail={(em) => addEmailToOpportunity(selected.id, em)}
          onRemoveEmail={(emailId) => removeEmailFromOpportunity(selected.id, emailId)}
          onCreateProposal={() => onCreateProposal?.(selected.id)}
          onCreateQuote={() => onCreateQuote?.(selected.id)}
          onOpenProposal={onOpenProposal}
          onOpenQuote={onOpenQuote}
        />
      ) : (
        <Board
          visible={visible}
          showClosed={showClosed}
          totalCount={opportunities.length}
          proposals={proposals}
          quotes={quotes}
          onOpen={setSelectedOpportunityId}
          onCreateFromEmail={createOpportunityFromEmail}
          onAddEmail={addEmailToOpportunity}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Board
   ══════════════════════════════════════════════════════════════════════ */

function Board({ visible, showClosed, totalCount, proposals, quotes,
                 onOpen, onCreateFromEmail, onAddEmail }) {
  const [dragOver, setDragOver] = useState(false);

  return (
    <div className="scroll" style={{ flex: 1, padding: 20, minWidth: 0 }}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        // Dropped on the background rather than a card — a new opportunity.
        e.preventDefault();
        setDragOver(false);
        const em = emailFromDrop(e);
        if (em) onCreateFromEmail(em);
      }}
    >
      {visible.length === 0 ? (
        <EmptyBoard showClosed={showClosed} totalCount={totalCount} dragOver={dragOver} />
      ) : (
        <>
          {dragOver && (
            <div style={{ marginBottom: 14, padding: "8px 12px", borderRadius: 6, fontSize: 11,
                          fontWeight: 600, color: "var(--strat)", background: "var(--strat-d)",
                          border: "1px dashed var(--strat)" }}>
              Drop on a card to attach · drop here to create a new opportunity
            </div>
          )}
          {/* auto-fill rather than a hard `repeat(3, 1fr)`: the board shares the
              width with the Inbox panel, so a fixed column count either
              overflows when the panel is open or leaves cards absurdly wide on a
              large monitor. At this app's pane widths this lands on three. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                        gap: 14, alignContent: "start" }}>
            {visible.map((o) => (
              <OpportunityCard key={o.id} opportunity={o}
                proposals={proposals} quotes={quotes}
                onOpen={() => onOpen(o.id)}
                onAddEmail={(em) => onAddEmail(o.id, em)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function EmptyBoard({ showClosed, totalCount, dragOver }) {
  /* An empty CLOSED board means nothing has been closed yet — quite different
     from having no opportunities at all, and the drag hint would be wrong. */
  if (showClosed) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text3)", fontSize: 12 }}>
        <div style={{ fontSize: 34, marginBottom: 10, opacity: 0.4 }}>📁</div>
        <div style={{ fontWeight: 600, color: "var(--text2)", marginBottom: 4 }}>Nothing closed yet</div>
        <div>{totalCount > 0
          ? "Close an opportunity as Won or Lost and it will appear here."
          : "No opportunities have been created yet."}</div>
      </div>
    );
  }
  return (
    <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text3)", fontSize: 12,
                  border: `2px dashed ${dragOver ? "var(--strat)" : "var(--border2)"}`, borderRadius: 10,
                  background: dragOver ? "var(--strat-d)" : "transparent", transition: "all 0.15s" }}>
      <div style={{ fontSize: 34, marginBottom: 10, opacity: 0.4 }}>💡</div>
      <div style={{ fontWeight: 600, color: "var(--text2)", marginBottom: 4 }}>No open opportunities</div>
      <div style={{ lineHeight: 1.6 }}>
        Drag an email from the inbox →<br />or click <strong>+ New opportunity</strong>
      </div>
    </div>
  );
}

function OpportunityCard({ opportunity: o, proposals, quotes, onOpen, onAddEmail }) {
  const [dragOver, setDragOver] = useState(false);

  const open   = isOpen(o);
  const colour = getOpportunityColour(o, proposals, quotes);
  const counts = opportunityCounts(o, proposals, quotes);

  /* Closed cards drop the pipeline colours entirely. Red/orange/green describe
     how far a LIVE deal has progressed; keeping them on a settled one would say
     a lost deal is still "in progress". */
  const style = open ? COLOUR_STYLE[colour] : CLOSED_STYLE[pipelineStatusOf(o)];
  const won   = pipelineStatusOf(o) === CLOSED_WON;

  return (
    <div onClick={onOpen}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy"; setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault(); e.stopPropagation(); setDragOver(false);
        const em = emailFromDrop(e);
        if (em) onAddEmail(em);
      }}
      title={open ? "Click to open · drop an email here to attach" : "Click to open"}
      style={{
        border: `1px solid ${dragOver ? "var(--strat)" : "var(--border)"}`,
        borderLeft: `4px solid ${style.line}`,
        borderRadius: 8,
        padding: 14,
        background: dragOver ? "var(--strat-d)" : (open ? style.tint : "var(--s2)"),
        cursor: "pointer",
        minHeight: 150,
        display: "flex",
        flexDirection: "column",
        opacity: open ? 1 : 0.85,
        transition: "border-color 0.15s, background 0.15s, box-shadow 0.15s",
        boxShadow: dragOver ? "0 0 0 3px rgba(52,152,219,0.12)" : "none",
      }}>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                    gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
                       textTransform: "uppercase", color: open ? style.line : style.fg,
                       background: open ? "white" : "var(--s3)", padding: "3px 8px",
                       borderRadius: 10, border: `1px solid ${open ? style.line : "var(--border)"}` }}>
          {open ? style.label : (won ? "✓ Won" : "Lost")}
        </span>
        <span style={{ fontSize: 10, color: "var(--text3)", flexShrink: 0 }}>
          {open ? fmtDate(o.updatedAt || o.createdAt) : `closed ${fmtDate(o.closedAt)}`}
        </span>
      </div>

      <div style={{ fontFamily: "var(--ff-head)", fontSize: 14, fontWeight: 700, lineHeight: 1.35,
                    color: "var(--text)", marginBottom: 3, letterSpacing: "-0.2px" }}>
        {o.title || "(untitled)"}
      </div>

      <div style={{ fontSize: 11, color: o.companyName ? "var(--strat)" : "var(--text3)",
                    fontWeight: o.companyName ? 600 : 400, marginBottom: 10 }}>
        {o.companyName || "No company set"}
      </div>

      {o.estValue > 0 && (
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text2)", marginBottom: 10 }}>
          ${Number(o.estValue).toLocaleString("en-AU")} <span style={{ fontSize: 9, fontWeight: 500, color: "var(--text3)" }}>est. ex GST</span>
        </div>
      )}

      {/* Counts sit at the card foot so every card's footer lines up regardless
          of how long its title ran. On a closed card they are dropped — once a
          deal is settled the tally stops being the thing you scan for. */}
      <div style={{ marginTop: "auto", paddingTop: 10, borderTop: "1px solid var(--border)",
                    display: "flex", gap: 12, fontSize: 10, color: "var(--text3)" }}>
        {open ? (
          <>
            <span title={`${counts.emails} attached email${counts.emails === 1 ? "" : "s"}`}>
              💬 {counts.emails}
            </span>
            <span title={`${counts.proposals} linked proposal${counts.proposals === 1 ? "" : "s"}`}
                  style={counts.proposals ? { color: "var(--text2)", fontWeight: 600 } : undefined}>
              📋 {counts.proposals} proposal{counts.proposals === 1 ? "" : "s"}
            </span>
            <span title={`${counts.quotes} linked quote${counts.quotes === 1 ? "" : "s"}`}
                  style={counts.quotes ? { color: "var(--text2)", fontWeight: 600 } : undefined}>
              💰 {counts.quotes} quote{counts.quotes === 1 ? "" : "s"}
            </span>
            {/* Only when there is one. A Lead is a pre-proposal, so a permanent
                "0 leads" on every card would be noise on the common case. */}
            {counts.leads > 0 && (
              <span title={`${counts.leads} linked lead${counts.leads === 1 ? "" : "s"}`}
                    style={{ color: "var(--text2)", fontWeight: 600 }}>
                🔎 {counts.leads} lead{counts.leads === 1 ? "" : "s"}
              </span>
            )}
          </>
        ) : (
          <span>Closed {o.closedAt ? fmtDateTime(o.closedAt) : "(date not recorded)"}</span>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Detail
   ══════════════════════════════════════════════════════════════════════ */

function OpportunityDetail({ opportunity: o, proposals, quotes, atCompanies,
                             onBack, onPatch, onDelete, onDropEmail, onRemoveEmail,
                             onCreateProposal, onCreateQuote, onOpenProposal, onOpenQuote }) {
  const [dragOver, setDragOver] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());

  const open   = isOpen(o);
  const colour = getOpportunityColour(o, proposals, quotes);
  const style  = open ? COLOUR_STYLE[colour] : CLOSED_STYLE[pipelineStatusOf(o)];

  const linkedProposals = getLinkedProposals(o.id, proposals);
  const linkedQuotes    = getLinkedQuotes(o.id, quotes);

  /* Oldest first — this is a conversation, and a thread is read top-down. The
     board sorts by recency because there you are scanning; here you are
     reading. Falls back to addedAt for an email with no send time. */
  const emails = [...(o.emails || [])].sort((a, b) => {
    const ta = new Date(a.time || 0).getTime() || a.addedAt || 0;
    const tb = new Date(b.time || 0).getTime() || b.addedAt || 0;
    return ta - tb;
  });

  const toggleEmail = (id) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const close = (won) => {
    const label = won ? "Won" : "Lost";
    if (!window.confirm(
      `Close "${o.title || "this opportunity"}" as ${label}?\n\n` +
      `It moves to the closed list and leaves the open board. ` +
      `You can reopen it from there if this was a mistake.`
    )) return;
    onPatch(closePatch(won));
  };

  return (
    <div className="scroll" style={{ flex: 1, padding: 20, minWidth: 0 }}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setDragOver(false);
        const em = emailFromDrop(e);
        if (em) onDropEmail(em);
      }}>

      {/* Back + title */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <button className="btn" style={{ fontSize: 11, flexShrink: 0 }} onClick={onBack}>← Board</button>
        <input type="text" value={o.title || ""}
          onChange={(e) => onPatch({ title: e.target.value })}
          placeholder="Opportunity title…"
          style={{ flex: 1, fontSize: 16, fontWeight: 700, fontFamily: "var(--ff-head)",
                   border: "1px solid transparent", background: "transparent", padding: "6px 10px",
                   color: "var(--text)", outline: "none", borderRadius: 6, letterSpacing: "-0.3px" }}
          onFocus={(e) => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "white"; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = "transparent"; e.currentTarget.style.background = "transparent"; }} />
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                       color: open ? style.line : style.fg, background: open ? style.tint : "var(--s3)",
                       border: `1px solid ${open ? style.line : "var(--border)"}`,
                       padding: "5px 12px", borderRadius: 12, flexShrink: 0 }}>
          {style.label}
        </span>
        <button className="btn btn-danger" style={{ fontSize: 11, flexShrink: 0 }}
                onClick={onDelete}>🗑 Delete</button>
      </div>

      {dragOver && (
        <div style={{ marginBottom: 12, padding: "8px 12px", borderRadius: 6, fontSize: 11,
                      fontWeight: 600, color: "var(--strat)", background: "var(--strat-d)",
                      border: "1px dashed var(--strat)" }}>
          Drop to attach this email to {o.title || "this opportunity"}
        </div>
      )}

      {/* Company / value */}
      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 12, marginBottom: 18 }}>
        <Field label="Company">
          <select className="inp" style={{ width: "100%", fontSize: 11, marginTop: 4, marginBottom: 0 }}
            value={o.companyId || ""}
            onChange={(e) => {
              const cid = e.target.value;
              const c = atCompanies.find((x) => String(x.id) === cid);
              onPatch({ companyId: cid, companyName: c?.companyName || "" });
            }}>
            <option value="">— Select company —</option>
            {[...atCompanies].sort((a, b) => (a.companyName || "").localeCompare(b.companyName || ""))
              .map((c) => <option key={c.id} value={String(c.id)}>{c.companyName}</option>)}
          </select>
        </Field>
        <Field label="Estimated value (ex GST)">
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
            <span style={{ fontSize: 13, color: "var(--text2)" }}>$</span>
            <input type="number" className="inp" style={{ flex: 1, marginBottom: 0, fontSize: 11, textAlign: "right" }}
              value={o.estValue || 0} min="0" step="100"
              onChange={(e) => onPatch({ estValue: e.target.value === "" ? 0 : Number(e.target.value) })} />
          </div>
        </Field>
      </div>

      {/* Pipeline actions. Closing is the ONE manual step in this feature —
          colour is computed, so this is deliberately the only button that
          changes where an opportunity lives. */}
      <div style={{ marginBottom: 18, padding: "12px 14px", borderRadius: 8,
                    border: `1px solid ${open ? "var(--border)" : style.line}`,
                    background: open ? "var(--s2)" : "white",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 12, flexWrap: "wrap" }}>
        {open ? (
          <>
            <div style={{ fontSize: 11, color: "var(--text3)", lineHeight: 1.5 }}>
              <strong style={{ color: "var(--text2)" }}>Open</strong> · showing{" "}
              <span style={{ color: style.line, fontWeight: 700 }}>{style.label.toLowerCase()}</span>
              {colour === "red"   && " — nothing linked yet"}
              {colour === "orange"&& " — proposal or quote in play"}
              {colour === "green" && " — a linked item has been won"}
              <div style={{ marginTop: 2, fontSize: 10 }}>
                Reaching green does not close the deal. Closing stays a deliberate step.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              <button className="btn btn-O" style={{ fontSize: 11 }}
                      onClick={() => close(true)}>✓ Close (Won)</button>
              <button className="btn" style={{ fontSize: 11 }}
                      onClick={() => close(false)}>✕ Close (Lost)</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 11, color: "var(--text3)", lineHeight: 1.5 }}>
              <strong style={{ color: style.fg, fontSize: 12 }}>{style.label}</strong>
              {o.closedAt && <> · {fmtDateTime(o.closedAt)}</>}
              <div style={{ marginTop: 2, fontSize: 10 }}>
                Hidden from the open board. Reopen if this was closed by mistake.
              </div>
            </div>
            <button className="btn btn-strat" style={{ fontSize: 11, flexShrink: 0 }}
                    onClick={() => onPatch(reopenPatch())}>↺ Reopen</button>
          </>
        )}
      </div>

      {/* Linked proposals + quotes */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 18 }}>
        {/* Leads ride in this list too — they are records in the same array and
            open in the same tab. The sub-line is what tells them apart, since a
            Lead has no recommendedPlan to show there. */}
        <LinkedList icon="📋" title="Linked proposals & leads" items={linkedProposals}
          labelOf={(p) => p.companyName || p.content?.customerName || p.title || "Untitled proposal"}
          subOf={(p) => (isLead(p) ? "Lead — pre-proposal" : (p.content?.recommendedPlan || ""))}
          onOpen={onOpenProposal}
          createLabel="Create proposal"
          onCreate={onCreateProposal} />
        <LinkedList icon="💰" title="Linked quotes" items={linkedQuotes}
          labelOf={(q) => q.title || "Untitled quote"}
          subOf={(q) => q.companyName || ""}
          onOpen={onOpenQuote}
          createLabel="Create quote"
          onCreate={onCreateQuote} />
      </div>

      {/* Notes */}
      <div style={{ marginBottom: 18 }}>
        <Label>Notes</Label>
        <textarea className="inp" value={o.notes || ""}
          onChange={(e) => onPatch({ notes: e.target.value })}
          placeholder="Background, requirements, decision criteria, follow-ups…"
          style={{ width: "100%", minHeight: 80, resize: "vertical", fontSize: 12, marginTop: 4,
                   marginBottom: 0, fontFamily: "inherit", lineHeight: 1.5 }} />
      </div>

      {/* Email history */}
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                      marginBottom: 8, gap: 10 }}>
          <Label>Email history ({emails.length}) · oldest first</Label>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {emails.length > 1 && (
              <button className="ico-btn" style={{ fontSize: 10, color: "var(--strat)", fontWeight: 600 }}
                onClick={() => setCollapsed(collapsed.size ? new Set() : new Set(emails.map((e) => e.id)))}>
                {collapsed.size ? "Expand all" : "Collapse all"}
              </button>
            )}
            <span style={{ fontSize: 9, color: "var(--text3)", fontStyle: "italic" }}>
              Drag emails from the inbox onto this page →
            </span>
          </div>
        </div>
        {emails.length === 0 ? (
          <div style={{ padding: "22px 16px", textAlign: "center", fontSize: 11, color: "var(--text3)",
                        fontStyle: "italic", border: "1px dashed var(--border2)", borderRadius: 6,
                        background: "var(--s2)" }}>
            No emails attached yet. Drag one from the inbox onto this page to start the record.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {emails.map((em, i) => (
              <EmailEntry key={em.id} email={em} index={i + 1} total={emails.length}
                collapsed={collapsed.has(em.id)}
                onToggle={() => toggleEmail(em.id)}
                onRemove={() => onRemoveEmail(em.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmailEntry({ email: em, index, total, collapsed, onToggle, onRemove }) {
  return (
    <div style={{ background: "white", border: "1px solid var(--border)", borderRadius: 6,
                  borderLeft: "3px solid var(--border2)" }}>
      {/* Header is the collapse control. The × is stopPropagation'd so removing
          an email never reads as "it collapsed and then vanished". */}
      <div onClick={onToggle} style={{ padding: "10px 12px", cursor: "pointer", display: "flex",
                                       justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 3 }}>
            <span style={{ fontSize: 9, color: "var(--text3)", fontWeight: 600, flexShrink: 0 }}>
              {index}/{total}
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text)", lineHeight: 1.4 }}>
              {em.subj || "(no subject)"}
            </span>
          </div>
          <div style={{ fontSize: 10, color: "var(--text3)", display: "flex", gap: 6, flexWrap: "wrap" }}>
            {em.from && <span>From <strong style={{ color: "var(--text2)" }}>{em.from}</strong></span>}
            {em.fromEmail && <span>&lt;{em.fromEmail}&gt;</span>}
            {em.time && <span>· {fmtDateTime(em.time)}</span>}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
          <span style={{ fontSize: 10, color: "var(--text3)", width: 14, textAlign: "center" }}>
            {collapsed ? "▸" : "▾"}
          </span>
          <button className="ico-btn" style={{ fontSize: 14, padding: "0 4px", color: "var(--danger)" }}
                  onClick={(e) => { e.stopPropagation(); onRemove(); }}
                  title="Remove email from opportunity">×</button>
        </div>
      </div>
      {!collapsed && em.body && (
        <div style={{ fontSize: 11, color: "var(--text2)", lineHeight: 1.55, whiteSpace: "pre-wrap",
                      maxHeight: 260, overflow: "auto", padding: "10px 12px",
                      borderTop: "1px solid var(--border)", background: "var(--s2)" }}>
          {em.body}
        </div>
      )}
      {!collapsed && !em.body && (
        <div style={{ fontSize: 10, color: "var(--text3)", fontStyle: "italic", padding: "8px 12px",
                      borderTop: "1px solid var(--border)" }}>
          No body was captured with this email.
        </div>
      )}
    </div>
  );
}

/* Linked proposals / quotes.
   The create action sits BELOW its own list as a full-width row rather than
   beside the heading. Each column is only half the detail width, so a heading
   and a button on one line left the button hard against the next column's
   heading and clipped at the right edge — and any nowrap/ellipsis tuning only
   moved which of the two got cut. A full-width row cannot collide with
   anything, and it still reads as belonging to the list it sits under. */
function LinkedList({ icon, title, items, labelOf, subOf, onOpen, onCreate, createLabel }) {
  return (
    <div>
      <Label>{title} ({items.length})</Label>
      <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 6 }}>
        {items.length === 0 ? (
          <div style={{ padding: "14px 12px", fontSize: 10, color: "var(--text3)", fontStyle: "italic",
                        border: "1px dashed var(--border2)", borderRadius: 6, background: "var(--s2)" }}>
            None linked yet.
          </div>
        ) : items.map((it) => {
          const st = itemStatus(it.status);
          return (
            <div key={it.id} onClick={onOpen ? () => onOpen(it.id) : undefined}
              style={{ padding: "8px 10px", background: "white", border: "1px solid var(--border)",
                       borderRadius: 6, display: "flex", alignItems: "center", gap: 8,
                       cursor: onOpen ? "pointer" : "default" }}
              title={onOpen ? "Open" : undefined}>
              <span style={{ fontSize: 11, flexShrink: 0 }}>{icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text)",
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {labelOf(it)}
                </div>
                {subOf(it) && (
                  <div style={{ fontSize: 9, color: "var(--text3)", whiteSpace: "nowrap",
                                overflow: "hidden", textOverflow: "ellipsis" }}>{subOf(it)}</div>
                )}
              </div>
              <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                             letterSpacing: "0.5px", color: st.fg, background: st.bg,
                             padding: "2px 7px", borderRadius: 10, flexShrink: 0 }}>{st.label}</span>
            </div>
          );
        })}

        {onCreate && (
          <button onClick={onCreate} title={`${createLabel} from this opportunity`}
            style={{ width: "100%", padding: "8px 10px", fontSize: 10, fontWeight: 600,
                     color: "var(--strat)", background: "transparent", cursor: "pointer",
                     border: "1px dashed var(--strat)", borderRadius: 6, textAlign: "center" }}>
            + {createLabel}
          </button>
        )}
      </div>
    </div>
  );
}

const Label = ({ children }) => (
  <label style={{ fontSize: 10, color: "var(--text3)", fontWeight: 600,
                  textTransform: "uppercase", letterSpacing: "0.5px" }}>{children}</label>
);

const Field = ({ label, children }) => (
  <div><Label>{label}</Label>{children}</div>
);
